import clsx from "clsx";
import {
  ChangeEvent,
  FormEvent,
  InputHTMLAttributes,
  ReactNode,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import CheckoutContext from "../../../../checkout-context";

type InputProps = {
  label?: string;
  invalidMessage?: string;
} & InputHTMLAttributes<HTMLInputElement>;

export function FormField({
  label,
  name,
  id = name,
  type = "text",
  placeholder,
  onChange,
  invalidMessage,
  ...props
}: InputProps) {
  const [isInvalid, setIsInvalid] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { textsMapping } = useContext(CheckoutContext);
  const labelBottom = useMemo(
    () => ["checkbox", "radio"].includes(type),
    [type]
  );

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    if (isInvalid) {
      setIsInvalid(!e.target.willValidate);
    }

    onChange?.(e);
  }

  function handleOnInvalid(e: FormEvent<HTMLInputElement>) {
    e.preventDefault(); // Prevent native validation messages
    setIsInvalid(true);
  }

  return (
    <div className={clsx("form-field", isInvalid && "is-invalid")}>
      {label && !labelBottom && <label htmlFor={id}>{label}</label>}
      <input
        id={id}
        name={name}
        type={type}
        placeholder={placeholder}
        onChange={handleChange}
        onInvalid={handleOnInvalid}
        {...props}
        ref={inputRef}
      />
      {label && labelBottom && <label htmlFor={id}>{label}</label>}
      {isInvalid && (
        <span className="field-validation-error">
          {invalidMessage || textsMapping.inputErrorMessageDefault}
        </span>
      )}
    </div>
  );
}

type GroupProps = { children: ReactNode };

function Group({ children }: GroupProps) {
  return <div className="form-field-group">{children}</div>;
}

type SectionProps = {
  children: ReactNode;
  title?: string;
  description?: string;
};

function Section({ children, title, description }: SectionProps) {
  return (
    <div className="form-field-section">
      {title && <h3 className="form-field-section__title">{title}</h3>}
      {description && (
        <p className="form-field-section__description">{description}</p>
      )}
      {children}
    </div>
  );
}

FormField.Group = Group;
FormField.Section = Section;
